import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ChatPanel = ({ isOpen, onClose, messages, onSendMessage, participantName }) => {
  const [messageText, setMessageText] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (e) => {
    e?.preventDefault();
    if (messageText?.trim()) {
      onSendMessage(messageText);
      setMessageText('');
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (!isOpen) return null;

  return (
    <div className="absolute top-0 right-0 bottom-0 w-96 bg-card border-l border-border shadow-xl flex flex-col z-40">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="MessageSquare" size={20} className="text-primary" />
          <h3 className="text-base font-semibold text-foreground">Session Chat</h3>
        </div>
        <Button
          variant="ghost"
          size="icon"
          iconName="X"
          onClick={onClose}
        />
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages?.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Icon name="MessageCircle" size={48} className="text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              No messages yet. Start the conversation!
            </p>
          </div>
        ) : (
          messages?.map((message) => (
            <div
              key={message?.id}
              className={`flex ${message?.sender === 'me' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-lg px-4 py-2 ${
                  message?.sender === 'me' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground'
                }`}
              >
                <p className="text-sm break-words">{message?.text}</p>
                <p
                  className={`text-xs mt-1 ${
                    message?.sender === 'me' ?'text-primary-foreground/70' :'text-muted-foreground'
                  }`}
                >
                  {formatTime(message?.timestamp)}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      {/* Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-border">
        <div className="flex items-end gap-2">
          <Input
            type="text"
            placeholder="Type a message..."
            value={messageText}
            onChange={(e) => setMessageText(e?.target?.value)}
            className="flex-1"
          />
          <Button
            type="submit"
            variant="default"
            size="icon"
            iconName="Send"
            disabled={!messageText?.trim()}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          All messages are encrypted and logged for compliance
        </p>
      </form>
    </div>
  );
};

export default ChatPanel;